import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
    BaseComponent,
    NotificationPreferenceChannel,
    UserNotificationPreferences,
    NotificationSettingsService
} from '@ridango/core';
import { AuthService } from '@ridango/core';
import { take, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, combineLatest } from 'rxjs';
import Env, { NotificationPreferenceConfig, NotificationPreferenceSetting } from '@ridango/core/env';
import { ApplicationFacade, CustomerFacade } from '@ridango/components';
import { ToastController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { openOsNotificationSettings } from '@ridango/components/lib/permissions';
import { AddEmailModalComponent } from './../add-email-modal/add-email-modal.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'rwidget-notification-settings',
    templateUrl: './notification-settings.component.html',
    styleUrls: ['./notification-settings.component.scss']
})
export class NotificationSettingsComponent extends BaseComponent implements OnInit, OnDestroy {
    assetsPath$ = this.applicationFacade.assetsPath$;
    subscribedNotificationsCount$ = new BehaviorSubject<number>(0);
    subscribedToggleState$ = new BehaviorSubject<boolean>(false);
    otherToggleState$ = new BehaviorSubject<boolean>(false);
    notificationPreferencesView: NotificationPreferenceConfig = {};
    @ViewChild('emailModal') emailModal: AddEmailModalComponent;
    userNotificationPref: UserNotificationPreferences;
    NotificationPreferenceChannel = NotificationPreferenceChannel;

    preserveOrder = () => 0;

    constructor(
        private readonly translate: TranslateService,
        private readonly notificationSettingsService: NotificationSettingsService,
        private readonly applicationFacade: ApplicationFacade,
        private readonly store$: Store,
        private readonly customerFacade: CustomerFacade,
        private readonly toastCtrl: ToastController,
        private readonly authService: AuthService,
        @Inject('ENV') public env: Env
    ) {
        super();
    }

    ngOnInit(): void {
        this.notificationPreferencesView = this.getNotificationPreferencesView();
        if (this.env.APP_NOTIFICATIONS?.HIDE_IN_APP_TOGGLE) {
            return;
        }

        this.loadNotificationPreferences();

        this.customerFacade.loaded$.pipe(take(1)).subscribe((loaded) => {
            if (!loaded) {
                this.customerFacade.getCustomerProfile();
            }
        });
    }

    handleBackClicked(): void {
        window.history.back();
    }

    ngOnDestroy() {
        this.subscribedNotificationsCount$.complete();
        this.subscribedToggleState$.complete();
        this.otherToggleState$.complete();
        super.ngOnDestroy();
    }

    openOsNotificationSettings() {
        this.store$.dispatch(openOsNotificationSettings());
    }

    loadNotificationPreferences(): void {
        combineLatest([
            this.notificationSettingsService.getNotificationPreferences(),
            this.notificationSettingsService.getDisturbanceSubscriptions()
        ])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe({
                next: ([userPref, subscriptions]) => {
                    this.userNotificationPref = userPref;
                    if (userPref.preferences && userPref.preferences.length > 0) {
                        const currentPref = userPref.preferences[0];
                        this.subscribedToggleState$.next(currentPref.settings.DISTURBANCE);
                        this.otherToggleState$.next(currentPref.settings.GENERAL);
                    }

                    const activeSubscriptionsCount = subscriptions.filter((sub) => sub.active).length;
                    this.subscribedNotificationsCount$.next(activeSubscriptionsCount);
                },
                error: (error) => {
                    console.error('Failed to load notification data:', error);
                }
            });
    }

    private getNotificationPreferencesView(): NotificationPreferenceConfig {
        const isMobileApp = !!this.env.IS_MOBILE_APP;
        const isAuthenticated = this.authService.getLoginStatus();
        if (isMobileApp && !isAuthenticated) {
            return { [NotificationPreferenceChannel.Push]: [] as NotificationPreferenceSetting[] };
        }
        return this.env.NOTIFICATION_PREFERENCES || {};
    }

    async presentToast(message: string) {
        const toast = await this.toastCtrl.create({
            message: 'ⓘ ' + message,
            duration: 5000,
            position: 'top',
            cssClass: 'custom-toast',
            buttons: [{ icon: 'close-circle', role: 'cancel' }]
        });
        await toast.present();
    }

    onChannelToggle(channel: NotificationPreferenceChannel | string, enabled: boolean): void {
        const current = this.userNotificationPref?.preferences ?? [];
        const configuredKeys: string[] = (this.env?.NOTIFICATION_PREFERENCES?.[channel] as string[]) || [];
        const updated = current.map((preference) => {
            if (preference.channel !== channel) {
                return preference;
            }
            const updatedSettings: any = { ...preference.settings };
            if (!enabled && configuredKeys?.length) {
                for (const key of configuredKeys) {
                    updatedSettings[key] = false;
                }
            }
            return { ...preference, enabled, settings: updatedSettings };
        });
        const payload: UserNotificationPreferences = { ...this.userNotificationPref, preferences: updated };
        this.userNotificationPref = payload;
        this.savePreferences(payload);
    }

    onChannelSettingToggle(
        channel: NotificationPreferenceChannel | string,
        settingType: string,
        enabled: boolean
    ): void {
        const current = this.userNotificationPref?.preferences ?? [];
        const updated = current.map((preference) =>
            preference.channel === channel
                ? { ...preference, settings: { ...preference.settings, [settingType]: enabled } }
                : preference
        );
        const payload: UserNotificationPreferences = { ...this.userNotificationPref, preferences: updated };
        this.userNotificationPref = payload;
        this.savePreferences(payload);
    }

    getChannelEnabled(channel: NotificationPreferenceChannel | string): boolean {
        return this.userNotificationPref?.preferences?.find((p) => p.channel === channel)?.enabled;
    }

    getSettingValue(channel: NotificationPreferenceChannel | string, settingType: string): boolean {
        const pref = this.userNotificationPref?.preferences?.find((p) => p.channel === channel);
        return !!pref?.settings?.[settingType];
    }

    private savePreferences(preferences: UserNotificationPreferences): void {
        this.notificationSettingsService
            .updateNotificationPreferences(preferences)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe({
                next: () => {
                    this.loadNotificationPreferences();
                },
                error: () => {
                    this.presentToast(this.translate.instant('NOTIFICATION_PREFERENCES_SAVE_FAILED')).then();
                }
            });
    }
}
